/**
 * The small Markdown a reply or note is written in.
 *
 * The composer is a rich-text editor, but what it saves is text: Markdown,
 * because everything downstream already handles text -- AI Proof, the
 * knowledgebase, search, the plain-text half of every email, a requester
 * replying from a phone. One renderer here turns it into HTML for both the
 * email and the ticket screen, so the two cannot disagree.
 *
 * DELIBERATELY SMALL. Paragraphs and line breaks, bold, italic, strike,
 * inline code, links, and bulleted and numbered lists (one level). No
 * headings, images, tables or raw HTML: a reply is a message, not a page,
 * and every extra construct is another way for an email client to mangle it.
 *
 * SAFE BY CONSTRUCTION. Everything is escaped first; the only tags that can
 * appear are the ones written below, and a link is kept only for http(s) and
 * mailto. Text that was never meant as Markdown -- every reply written before
 * the editor -- renders as the paragraphs it always was.
 */

// A backslash at the end of a line is a hard line break: the editor writes
// one for every Shift+Enter, and for every line break pasted from Outlook.
const ESCAPABLE = /\\([\\`*_{}[\]()#+\-.!>~|\n])/g;
const PH = '\u0000';

export function escapeHtmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

interface Styles {
  p: string;
  ul: string;
  ol: string;
  li: string;
  code: string;
  a: string;
}

const SCREEN: Styles = { p: '', ul: '', ol: '', li: '', code: '', a: '' };
// Inline styles: email clients strip <style> blocks.
const EMAIL: Styles = {
  p: 'margin:0 0 12px',
  ul: 'margin:0 0 12px;padding-left:22px',
  ol: 'margin:0 0 12px;padding-left:22px',
  li: 'margin:0 0 4px',
  code: 'font-family:Consolas,Menlo,monospace;background:#f3f4f6;padding:1px 4px;border-radius:3px',
  a: 'color:#0b6e63',
};

const attr = (style: string) => (style ? ` style="${style}"` : '');

/** Inline Markdown to HTML. `s` is raw; everything is escaped here. */
function inline(raw: string, st: Styles): string {
  // Backslash escapes become placeholders so nothing below reads them as syntax.
  const escaped: string[] = [];
  let s = raw.replace(ESCAPABLE, (_m, c: string) => {
    if (c === '\n') return c;
    escaped.push(c);
    return `${PH}e${escaped.length - 1}${PH}`;
  });
  // Code spans are literal: set aside before any other syntax is read.
  const code: string[] = [];
  s = s.replace(/`([^`\n]+)`/g, (_m, c: string) => {
    code.push(c);
    return `${PH}c${code.length - 1}${PH}`;
  });
  // Links, also set aside, so their URLs are not read as emphasis.
  const links: { text: string; href: string }[] = [];
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (m, text: string, href: string) => {
    if (!/^(https?:\/\/|mailto:)/i.test(href)) return m;
    links.push({ text, href });
    return `${PH}l${links.length - 1}${PH}`;
  });

  s = escapeHtmlText(s);
  s = s
    .replace(/\*\*([^*\n](?:[^*\n]|\*(?!\*))*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_\n]+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*([^*\s](?:[^*\n]*?[^*\s])?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\s](?:[^_\n]*?[^_\s])?)_(?!\w)/g, '$1<em>$2</em>')
    .replace(/~~([^~\n]+?)~~/g, '<s>$1</s>');

  const restore = (t: string): string =>
    t.replace(new RegExp(`${PH}([ecl])(\\d+)${PH}`, 'g'), (_m, kind: string, i: string) => {
      const n = Number(i);
      if (kind === 'e') return escapeHtmlText(escaped[n]!);
      if (kind === 'c') return `<code${attr(st.code)}>${escapeHtmlText(restoreEscapes(code[n]!, escaped))}</code>`;
      // Link text is shown as written, without emphasis of its own.
      const l = links[n]!;
      const text = restoreEscapes(l.text, escaped).replace(new RegExp(`${PH}c(\\d+)${PH}`, 'g'), (_x, j: string) => code[Number(j)]!);
      return `<a href="${escapeHtmlText(restoreEscapes(l.href, escaped))}"${attr(st.a)}>${escapeHtmlText(text)}</a>`;
    });
  return restore(s).replace(/\n/g, '<br>');
}

function restoreEscapes(s: string, escaped: string[]): string {
  return s.replace(new RegExp(`${PH}e(\\d+)${PH}`, 'g'), (_m, i: string) => escaped[Number(i)]!);
}

type Block = { kind: 'p'; lines: string[] } | { kind: 'ul' | 'ol'; items: string[]; start: number };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBER = /^\s*(\d{1,9})[.)]\s+(.*)$/;

function blocks(md: string): Block[] {
  const out: Block[] = [];
  let cur: Block | null = null;
  const flush = () => {
    if (cur) out.push(cur);
    cur = null;
  };
  for (const line of md.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.trim() === '') {
      flush();
      continue;
    }
    const b = BULLET.exec(line);
    const n = b ? null : NUMBER.exec(line);
    if (b || n) {
      const kind = b ? 'ul' : 'ol';
      const text = b ? b[1]! : n![2]!;
      if (!cur || cur.kind !== kind) {
        flush();
        cur = { kind, items: [], start: n ? Number(n[1]) : 1 };
      }
      cur.items.push(text);
      continue;
    }
    if (cur && cur.kind !== 'p') {
      // A line straight under a list item, with no blank line, continues it.
      cur.items[cur.items.length - 1] += `\n${line.trim()}`;
      continue;
    }
    if (!cur) cur = { kind: 'p', lines: [] };
    (cur as { kind: 'p'; lines: string[] }).lines.push(line);
  }
  flush();
  return out;
}

/** Markdown to HTML: `email` adds the inline styles mail clients need. */
export function markdownToHtml(md: string, opts: { email?: boolean } = {}): string {
  const st = opts.email ? EMAIL : SCREEN;
  return blocks(md)
    .map((b) => {
      if (b.kind === 'p') return `<p${attr(st.p)}>${inline(b.lines.join('\n'), st)}</p>`;
      const start = b.kind === 'ol' && b.start !== 1 ? ` start="${b.start}"` : '';
      const items = b.items.map((i) => `<li${attr(st.li)}>${inline(i, st)}</li>`).join('');
      return `<${b.kind}${start}${attr(b.kind === 'ul' ? st.ul : st.ol)}>${items}</${b.kind}>`;
    })
    .join('');
}

/**
 * Markdown to plain text, for the text half of an email and anywhere markers
 * would be noise: emphasis markers and escapes go, a link keeps its address
 * in brackets, lists keep their bullets and numbers.
 */
export function markdownToText(md: string): string {
  const escaped: string[] = [];
  let s = md.replace(/\r\n?/g, '\n').replace(ESCAPABLE, (_m, c: string) => {
    if (c === '\n') return c;
    escaped.push(c);
    return `${PH}e${escaped.length - 1}${PH}`;
  });
  s = s
    .replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^)\s]+)\)/gi, (_m, t: string, h: string) =>
      t === h || `mailto:${t}` === h ? t : `${t} (${h.replace(/^mailto:/i, '')})`,
    )
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/\*\*([^*\n]+?)\*\*/g, '$1')
    .replace(/__([^_\n]+?)__/g, '$1')
    .replace(/(^|[^*\w])\*([^*\s](?:[^*\n]*?[^*\s])?)\*(?!\*)/g, '$1$2')
    .replace(/(^|[^_\w])_([^_\s](?:[^_\n]*?[^_\s])?)_(?!\w)/g, '$1$2')
    .replace(/~~([^~\n]+?)~~/g, '$1')
    .replace(/^(\s*)[*+]\s+/gm, '$1- ');
  return restoreEscapes(s, escaped).trim();
}
